import { useMutation, useQueryClient } from '@tanstack/react-query';
import { editAppointmentRecord } from '@/lib/services/appointmentModalService';
import type { AppointmentRecord } from '@/lib/types/appointment';
import { getAppointmentRegistry } from '@/lib/appointment/appointmentRegistrySingleton';
import { invalidateRecordCaches } from '@/lib/appointment/recordCaches';

/**
 * Custom hook to edit an appointment record using React Query mutation.
 * This hook calls the editAppointmentRecord service function to send the updated
 * appointment data to the backend and invalidates relevant cache keys on success.
 */
export function useEditAppointmentRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (appointment: AppointmentRecord) => editAppointmentRecord(appointment),
    // Optimistic removal when the edit payload marks the record as deleted
    onMutate: async (vars) => {
      const registry = getAppointmentRegistry();
      const id = vars?.id ?? vars?.visit_id ?? vars?.external_id;
      // Snapshot for rollback
      const previous = id ? registry.getAppointmentByIdFull?.(String(id)) : undefined;
      if ((vars as any)?.deleted === 1 && id) {
        registry.removeById(id);
      }
      return { previous };
    },
    onSuccess: (data, vars) => {
      // Merge the API response back into the registry to reflect paid_full status immediately
      // The response contains the updated appointment data including paid_full
      const registry = getAppointmentRegistry();
      const mergedData = data?.data ?? (data && typeof data === 'object' && data.id ? data : null);
      if (mergedData) {
        registry.merge(mergedData);
      }

      // Also update the query cache directly so isPaidFull updates immediately
      // without waiting for the invalidate + refetch cycle
      if (vars.id) {
        const updatedAppointment = registry.getAppointmentById(String(vars.id));
        if (updatedAppointment) {
          queryClient.setQueryData(["appointmentPaymentInfo", vars.id], updatedAppointment);
          queryClient.setQueryData(["appointmentDetails", vars.id], updatedAppointment);
        }
      }

      // Week lists, the staff-scoped variants, the day report and the record's own
      // caches — shared with the drag & drop move so the two stay in step.
      invalidateRecordCaches(queryClient, {
        recordId: vars.id,
        dates: [vars.date],
        staffIds: [
          Number((vars as any).staff_id ?? (vars as any).master_id ?? (vars as any).specialistId),
        ],
      });
    },
    onError: (error, _vars, ctx) => {
      console.error('Failed to edit appointment:', error);
      // Rollback optimistic removal if needed
      if (ctx?.previous?.id) {
        try {
          getAppointmentRegistry().merge(ctx.previous);
        } catch { /* noop */ }
      }
    },
  });
}
